/**
 * OSS 直传上传工具函数（兼容阿里云 OSS 旧版表单直传和 OSS4 Web 直传）
 * 支持图片和视频的直传上传
 */

import { readOssSignature } from './api';

const DEFAULT_DIR = 'public/videos/';
const DEFAULT_SUCCESS_ACTION_STATUS = '200';
const OSS_V4_SIGNATURE_VERSION = 'OSS4-HMAC-SHA256';

/**
 * 随机生成文件名（保留扩展名）
 * @param {string} fileName 原文件名
 * @param {number} len 随机字符长度，默认 18
 * @returns {string|null} 随机文件名
 */
const randomFileName = (fileName, len = 18) => {
  const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  const maxPos = chars.length;
  const pos = fileName.lastIndexOf('.');

  if (pos === -1) return null;

  let str = '';
  for (let i = 0; i < len; i++) {
    str += chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return str + fileName.substring(pos);
};

const normalizeDir = (dir = DEFAULT_DIR) => {
  if (!dir) return '';
  return dir.endsWith('/') ? dir : `${dir}/`;
};

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const joinObjectKey = (dir, fileName) => {
  const normalizedDir = normalizeDir(dir);
  return `${normalizedDir}${fileName}`;
};

const buildObjectUrl = (host, objectKey) => {
  if (!host || !objectKey) return '';
  return `${String(host).replace(/\/$/, '')}/${objectKey.replace(/^\//, '')}`;
};

const normalizeUploadedVideoData = (data = {}, fallbackData = {}) => {
  const url = data.url || data.ossVideoUrl || data.oss_video_url || data.name || fallbackData.url || '';
  const videoCoverUrl = data.videoCoverUrl || data.video_cover_url || data.cover_url || data.coverUrl || '';
  const id = typeof data.id === 'number' ? data.id : Number(data.id);

  return {
    ...fallbackData,
    ...data,
    url,
    ossVideoUrl: data.ossVideoUrl || data.oss_video_url || url,
    videoCoverUrl,
    cover_url: data.cover_url || videoCoverUrl,
    id: Number.isNaN(id) ? data.id : id,
  };
};

const parseOssResponse = (xhr) => {
  const responseText = xhr.responseText || xhr.response || '';

  let attachment;
  try {
    attachment = JSON.parse(responseText);
  } catch (error) {
    throw new Error('OSS 上传成功，但未返回有效附件记录');
  }
  if (!attachment?.id || !Number.isSafeInteger(Number(attachment.id)) || Number(attachment.id) <= 0) {
    throw new Error('OSS 上传成功，但未返回有效附件记录');
  }
  return attachment;
};

const parseOssError = (xhr) => {
  const responseText = xhr.responseText || xhr.response || '';
  if (!responseText) {
    return xhr.status === 0 ? '网络异常，上传失败' : '上传失败';
  }

  const codeMatch = responseText.match(/<Code>([^<]+)<\/Code>/i);
  const messageMatch = responseText.match(/<Message>([^<]+)<\/Message>/i);
  const details = [codeMatch?.[1], messageMatch?.[1]].filter(Boolean).join(': ');

  return details || responseText || '上传失败';
};

/**
 * 通过 XMLHttpRequest 上传文件到 OSS
 * @param {string} url 上传地址（OSS host）
 * @param {Object<string, string>} headers 请求头
 * @param {FormData} formData 表单数据
 * @param {Function} onProgress 进度回调
 * @returns {Promise<{code: number, msg: string, data: any}>} 上传结果
 */
const uploadToOss = (url, headers = {}, formData, onProgress = () => {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (xhr.upload) {
      xhr.upload.onprogress = (event) => {
        const loaded = event.loaded || 0;
        const total = event.total || 1;
        const percent = Math.floor((loaded / total) * 100);
        onProgress(percent);
      };
    }

    xhr.onload = () => {
      if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
        try {
          return resolve({
            code: 0,
            msg: '上传成功',
            data: parseOssResponse(xhr),
          });
        } catch (error) {
          return reject(error);
        }
      }

      return reject({
        code: xhr.status || 500,
        msg: parseOssError(xhr),
      });
    };

    xhr.onerror = () => {
      return reject({
        code: xhr.status || 500,
        msg: parseOssError(xhr),
      });
    };

    xhr.open('POST', url, true);

    Object.keys(headers).forEach((key) => {
      if (headers[key] !== undefined && headers[key] !== null) {
        xhr.setRequestHeader(key, headers[key]);
      }
    });

    xhr.send(formData);
  });
};

const normalizeUploadOptions = (fileOrOptions, legacyDir = DEFAULT_DIR, legacyOnProgress) => {
  if (isPlainObject(fileOrOptions) && fileOrOptions.file) {
    return {
      file: fileOrOptions.file,
      dir: fileOrOptions.dir || DEFAULT_DIR,
      onProgress: fileOrOptions.onProgress,
      fileName: fileOrOptions.fileName,
      useRandomFileName: fileOrOptions.useRandomFileName !== false,
      forbidOverwrite: Boolean(fileOrOptions.forbidOverwrite),
      successActionStatus: String(fileOrOptions.successActionStatus || DEFAULT_SUCCESS_ACTION_STATUS),
      headers: fileOrOptions.headers || {},
      signatureParams: fileOrOptions.signatureParams || {},
    };
  }

  return {
    file: fileOrOptions,
    dir: legacyDir || DEFAULT_DIR,
    onProgress: legacyOnProgress,
    fileName: undefined,
    useRandomFileName: true,
    forbidOverwrite: false,
    successActionStatus: DEFAULT_SUCCESS_ACTION_STATUS,
    headers: {},
    signatureParams: {},
  };
};

const normalizeSignatureFields = (signatureData = {}, originalFileName = '') => {
  const host = signatureData.host;
  const dir = normalizeDir(signatureData.dir || signatureData.uploadDir || '');

  if (signatureData.x_oss_credential || signatureData['x-oss-credential']) {
    // 部分 Web 签名仅约束目录前缀；优先使用签发的 key，否则在后端目录下生成。
    const generatedFileName = !signatureData.key && dir ? randomFileName(originalFileName) : null;
    return {
      mode: 'oss4',
      host,
      dir,
      formFields: {
        key: signatureData.key || (generatedFileName ? joinObjectKey(dir, generatedFileName) : undefined),
        policy: signatureData.policy,
        'x-oss-signature': signatureData.signature || signatureData.x_oss_signature || signatureData['x-oss-signature'],
        'x-oss-signature-version': signatureData.x_oss_signature_version || signatureData['x-oss-signature-version'] || OSS_V4_SIGNATURE_VERSION,
        'x-oss-credential': signatureData.x_oss_credential || signatureData['x-oss-credential'],
        'x-oss-date': signatureData.x_oss_date || signatureData['x-oss-date'],
        'x-oss-security-token': signatureData.security_token || signatureData.x_oss_security_token || signatureData['x-oss-security-token'],
        callback: signatureData.callback,
        'x-oss-forbid-overwrite': signatureData['x-oss-forbid-overwrite'],
      },
      requiredFields: ['host', 'key', 'policy', 'x-oss-signature', 'x-oss-credential', 'x-oss-date', 'callback'],
    };
  }

  return {
    mode: 'legacy',
    host,
    dir,
    formFields: {
      policy: signatureData.policy,
      OSSAccessKeyId: signatureData.accessid,
      signature: signatureData.signature,
      callback: signatureData.callback,
    },
    requiredFields: ['host', 'policy', 'OSSAccessKeyId', 'signature'],
  };
};

const appendFormFields = (formData, formFields, mode, fileName) => {
  Object.entries(formFields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'callback') {
      formData.append(key, mode === 'legacy' ? buildLegacyCallback(value, fileName) : value);
      return;
    }
    formData.append(key, value);
  });
};

const getMissingRequiredFields = ({ host, formFields, requiredFields }) => {
  return requiredFields.filter((field) => {
    if (field === 'host') return !host;
    return formFields[field] === undefined || formFields[field] === null || formFields[field] === '';
  });
};

const buildLegacyCallback = (callback, fileName) => {
  if (!callback) return '';

  try {
    const callbackData = JSON.parse(atob(callback));
    const oldCallbackUrl = callbackData.callbackUrl;
    const newCallbackData = [1, 2, 3, 4, 5]
      .map(
        (value) =>
          `${oldCallbackUrl}?t=${value}&filename=${encodeURIComponent(fileName)}`
      )
      .join(';');
    callbackData.callbackUrl = newCallbackData;
    return btoa(JSON.stringify(callbackData));
  } catch (error) {
    console.warn('[uploadOssFile] callback 处理失败:', error);
    return callback;
  }
};

/**
 * 上传单个文件到 OSS
 * 新签名：uploadOssFile({ file, dir, onProgress, fileName, useRandomFileName, forbidOverwrite, successActionStatus, headers, signatureParams })
 * 兼容旧签名：uploadOssFile(file, dir, onProgress)
 * @returns {Promise<{code: number, msg: string, data?: any}>} 上传结果
 */
export const uploadOssFile = async (fileOrOptions, legacyDir = DEFAULT_DIR, legacyOnProgress) => {
  const options = normalizeUploadOptions(fileOrOptions, legacyDir, legacyOnProgress);
  const {
    file,
    dir,
    onProgress,
    fileName,
    useRandomFileName,
    forbidOverwrite,
    successActionStatus,
    headers,
    signatureParams,
  } = options;

  if (!file) {
    return {
      code: -1,
      msg: '缺少待上传文件',
    };
  }

  try {
    const signatureRes = await readOssSignature({
      ...signatureParams,
      fileName: file.name,
    });

    if (signatureRes.code !== 0) {
      console.error('[uploadOssFile] 获取签名失败:', signatureRes);
      return {
        code: signatureRes.code || -1,
        msg: signatureRes.msg || signatureRes.message || '获取 OSS 签名失败',
      };
    }

    const normalizedSignature = normalizeSignatureFields(signatureRes.data || {}, file.name);
    const { mode, host, dir: ossDir, formFields, requiredFields } = normalizedSignature;
    const missingFields = getMissingRequiredFields({ host, formFields, requiredFields });

    if (missingFields.length > 0) {
      console.error('[uploadOssFile] 签名数据不完整:', {
        mode,
        missingFields,
        signatureData: signatureRes.data,
      });
      return {
        code: -1,
        msg: `签名数据不完整: ${missingFields.join(', ')}`,
      };
    }

    const resolvedFileName = mode === 'oss4' ? file.name : fileName || (useRandomFileName ? randomFileName(file.name) : file.name);
    if (!resolvedFileName) {
      console.error('[uploadOssFile] 文件没有扩展名:', file.name);
      return {
        code: -1,
        msg: '文件必须有扩展名',
      };
    }

    const objectKey = mode === 'oss4' ? formFields.key : joinObjectKey(ossDir || dir, resolvedFileName);
    const formData = new FormData();
    const requestHeaders = { ...headers };
    Object.keys(requestHeaders).forEach((key) => {
      if (key.toLowerCase() === 'content-type') delete requestHeaders[key];
    });

    if (mode === 'legacy') {
      formData.append('key', objectKey);
      formData.append('success_action_status', successActionStatus);
      formData.append('name', objectKey);
    }

    appendFormFields(formData, formFields, mode, resolvedFileName);

    if (mode === 'oss4') {
      const extension = file.name.split('.').pop().toLowerCase();
      const mimeTypes = { mp4: 'video/mp4', mov: 'video/quicktime' };
      const contentType = mimeTypes[extension];
      if (!contentType) {
        return { code: -1, msg: '仅支持 MP4、MOV 格式的视频' };
      }
      formData.append('Content-Type', contentType);
    } else if (forbidOverwrite) {
      requestHeaders['x-oss-forbid-overwrite'] = 'true';
    }

    formData.append('file', file, resolvedFileName);

    const fallbackData = {
      name: objectKey,
      url: buildObjectUrl(host, objectKey),
      ossVideoUrl: buildObjectUrl(host, objectKey),
    };

    const result = await uploadToOss(host, requestHeaders, formData, onProgress);

    if (result.code === 0) {
      const mergedData = normalizeUploadedVideoData(result.data || {}, fallbackData);
      console.log('[uploadOssFile] 上传成功:', {
        mode,
        fileName: resolvedFileName,
        objectKey,
        url: mergedData.url,
      });
      return {
        code: 0,
        msg: '上传成功',
        data: mergedData,
      };
    }

    console.error('[uploadOssFile] 上传失败:', result);
    return result;
  } catch (error) {
    console.error('[uploadOssFile] 异常错误:', error);
    return {
      code: error?.code || -1,
      msg: error?.msg || error?.message || '上传异常',
    };
  }
};

/**
 * 批量上传文件到 OSS（并发）
 * @param {File[]} files 文件数组
 * @param {string|Object} dir 上传目录或配置对象
 * @returns {Promise<Array<{code: number, msg: string, data?: any}>>} 上传结果数组
 */
export const uploadOssFiles = async (files, dir = DEFAULT_DIR) => {
  try {
    const uploadOptions = isPlainObject(dir) ? dir : { dir };
    const promises = files.map((file) => uploadOssFile({
      ...uploadOptions,
      file,
    }));

    return await Promise.allSettled(promises).then((results) => {
      return results.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value;
        }
        return {
          code: -1,
          msg: result.reason?.message || '上传失败',
        };
      });
    });
  } catch (error) {
    console.error('[uploadOssFiles] 异常:', error);
    return [
      {
        code: -1,
        msg: error?.message || '批量上传异常',
      },
    ];
  }
};
